// Completed writes matter too: a settled in-memory fallback cannot survive navigation.
const memoryOnlyRecords = new Set<string>();
export function markWorkspaceDraftDurability(key: string, durable: boolean) {
  if (durable) memoryOnlyRecords.delete(key);
  else memoryOnlyRecords.add(key);
}

const pending = new Set<Promise<void>>();
const scheduled = new Map<string, { timer: ReturnType<typeof setTimeout>; run: () => void }>();
export function trackWorkspaceDraftWrite(write: Promise<void>) {
  pending.add(write);
  void write.then(
    () => pending.delete(write),
    () => pending.delete(write),
  );
}
export function cancelScheduledWorkspaceDraft(key: string) {
  const current = scheduled.get(key);
  if (current) clearTimeout(current.timer);
  scheduled.delete(key);
}
export function cancelAllScheduledWorkspaceDrafts() {
  for (const key of scheduled.keys()) cancelScheduledWorkspaceDraft(key);
}
export function scheduleWorkspaceDraftWrite(key: string, write: () => Promise<void>) {
  cancelScheduledWorkspaceDraft(key);
  const run = () => {
    cancelScheduledWorkspaceDraft(key);
    trackWorkspaceDraftWrite(write());
  };
  scheduled.set(key, { timer: setTimeout(run, 300), run });
}
export async function flushWorkspaceDrafts(): Promise<{ durable: boolean }> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const flush = async () => {
    do {
      for (const item of [...scheduled.values()]) item.run();
      await Promise.all([...pending]);
      // Staging completion schedules a React draft update and its persistence effect.
      await new Promise((resolve) => setTimeout(resolve, 0));
    } while (pending.size > 0 || scheduled.size > 0);
  };
  try {
    await Promise.race([
      flush(),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(
          () => reject(new Error('Draft persistence timed out. Please retry.')),
          5000,
        );
      }),
    ]);
    return { durable: memoryOnlyRecords.size === 0 };
  } finally {
    clearTimeout(timeout);
  }
}
